import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { AppDispatch, RootState } from 'store/store';
import { hasPermission } from 'utils/utils';
import dayjs from 'dayjs';
import { DATE_TIME_FORMAT } from 'constants/dates';
import DateRangeLocationFilter from 'components/customAntd/DateRangeLocationFilter';
import { OpPage } from 'components/customAntd/OpPage/OpPage';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { fetchVisits, setVisitsEndDate, setSelectedVisit, setVisitsStartDate } from 'store/slices/visitsSlice';
import { fetchAltaVideoIntegrationData, fetchVideoDevices, fetchDevicesByWorkflow, botLogoutAltaVideo } from 'store/slices/altaVideoSlice';
import VisitorsDrawer from './drawer/VisitorsDrawer';
import VisitorsTable from './VisitorsTable';
import { useTranslation } from 'react-i18next';

const Visitors: React.FC = () => {
    const dispatch: AppDispatch = useDispatch();

    const location = useLocation();
    const navigate = useNavigate();

    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const locations = useSelector((state: RootState) => state.locations.locations);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { isAltaVideoInstalled } = useSelector((state: RootState) => state.altaVideo);

    const [isVisitorsDrawerOpen, setIsVisitorsDrawerOpen] = useState(false);
    const [isProfileDrawerOpen, setIsProfileDrawerOpen] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);

    const { t } = useTranslation();

    const hasVisitorsBulkRead = hasPermission(tokenScopeList, orgId, 'o', 'visitorsbulk:r');

    // Check if a date is passed from the navigation
    const selectedDateFromState = location.state?.selectedDate;

    const selectedLocationIdRef = useRef<number>(globalLocationId!);
    const startDateRef = useRef(
        selectedDateFromState
            ? dayjs(selectedDateFromState).startOf('day').format(DATE_TIME_FORMAT)
            : dayjs().startOf('day').format(DATE_TIME_FORMAT)
    );
    const endDateRef = useRef(
        selectedDateFromState
            ? dayjs(selectedDateFromState).endOf('day').format(DATE_TIME_FORMAT)
            : dayjs().endOf('day').format(DATE_TIME_FORMAT)
    );

    const fetchVisitsData = async () => {
        setLoading(true);
        await dispatch(setVisitsStartDate(startDateRef.current));
        await dispatch(setVisitsEndDate(endDateRef.current));
        await dispatch(fetchVisits({ orgId }));
        setLoading(false);
    };

    useEffect(() => {
        fetchVisitsData();
        // eslint-disable-next-line
    }, [orgId]);

    useEffect(() => {
        if (!orgId) return;
        dispatch(fetchAltaVideoIntegrationData(orgId));
    }, [dispatch, orgId]);

    // Fetch device infos
    useEffect(() => {
        if (!orgId || !isAltaVideoInstalled) return;

        // Fetch existing video devices
        dispatch(fetchVideoDevices(orgId));

        if (globalLocationId && globalLocationId !== -1) { // If location is selected, fetch devices by workflow
            dispatch(fetchDevicesByWorkflow({ orgId, siteId: globalLocationId }));
        } else { // Fetch devices by workflow for all locations
            locations.forEach(site => {
                dispatch(fetchDevicesByWorkflow({ orgId, siteId: site.id }));
            });
        }
        // eslint-disable-next-line
    }, [dispatch, orgId, isAltaVideoInstalled]);

    // Log in bot-user when the user leaves the "Visitor" page
    useEffect(() => {
        return () => {
            if (isAltaVideoInstalled) {
                dispatch(botLogoutAltaVideo());
            }
        };
    }, [dispatch, isAltaVideoInstalled]);

    // Logout bot-user when the user leaves the browswer
    useEffect(() => {
        const logout = () => {
            if (!isAltaVideoInstalled) return;
            dispatch(botLogoutAltaVideo());
        }

        const onHide = () => {
            if (document.visibilityState === "hidden") logout();
        }

        window.addEventListener("blur", logout);
        document.addEventListener("visibilitychange", onHide);

        return () => {
            window.removeEventListener("blur", logout);
            document.removeEventListener("visibilitychange", onHide);
        }

    }, [dispatch, isAltaVideoInstalled]);


    const handleDateRangeLocationFilter = (locationId: number, startDate: string, endDate: string) => {
        selectedLocationIdRef.current = locationId;
        startDateRef.current = startDate;
        endDateRef.current = endDate;

        if (globalLocationId && globalLocationId !== -1) { // If location is selected, fetch devices by workflow
            dispatch(fetchDevicesByWorkflow({ orgId, siteId: globalLocationId }));
        } else { // Fetch devices from all locations
            locations.forEach(site => {
                dispatch(fetchDevicesByWorkflow({ orgId, siteId: site.id }));
            });
        }
        fetchVisitsData();
    };

    const handleDrawerClose = () => {
        setIsVisitorsDrawerOpen(false);
        dispatch(setSelectedVisit(null));
    };

    const handleBulkScheduleClick = () => {
        navigate('/visitors/bulkSchedule');
    };

    return (
        <OpPage
            title={t('visitors.title')}
            tooltip={t('visitors.tooltip')}
            contentRight={
                (hasVisitorsBulkRead && (
                    <OpButton type="default" onClick={handleBulkScheduleClick}>
                        {t('bulkSchedule.title')}
                    </OpButton>
                ))
            }
        >
            <OpSpace direction="vertical" size="middle" style={{ display: 'flex' }}>
                <DateRangeLocationFilter
                    onDateRangeLocationFilter={handleDateRangeLocationFilter}
                    initialStartDate={startDateRef.current}
                    initialEndDate={endDateRef.current}
                />
                <VisitorsTable
                    openVisitorsDrawer={() => setIsVisitorsDrawerOpen(true)}
                    openProfileDrawer={() => setIsProfileDrawerOpen(true)}
                    loading={loading}
                />
            </OpSpace>
            {(isVisitorsDrawerOpen) && (
                <VisitorsDrawer
                    open={isVisitorsDrawerOpen}
                    isProfileDrawerOpen={isProfileDrawerOpen}
                    onClose={handleDrawerClose}
                    setIsProfileDrawerOpen={setIsProfileDrawerOpen}
                />
            )}
        </OpPage>
    );
}

export default Visitors;